import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('crm_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Un jeton expiré doit ramener à la connexion, sinon chaque écran échoue en
// silence et en boucle. L'échange OIDC est exclu : son 401 doit remonter au
// contexte d'authentification pour être affiché, pas déclencher un rechargement
// qui masquerait la cause.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';
    if (error.response?.status === 401 && !url.includes('/auth/oidc')) {
      localStorage.removeItem('crm_token');
      localStorage.removeItem('crm_user');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Auth API — Keycloak (realm elika)
export const authAPI = {
  getConfig: () => api.get('/auth/config'),
  me: () => api.get('/auth/me'),
  /** Échange l'access token Keycloak contre le jeton applicatif. */
  exchange: (access_token) => api.post('/auth/oidc', { access_token }),
};

// Users API — miroir local des comptes Keycloak.
// Pas de création ni de changement de rôle : les comptes et les droits sont
// administrés dans le backoffice de portal.elikateam.com.
export const usersAPI = {
  list: () => api.get('/users'),
  setActive: (id, is_active) => api.put(`/users/${id}`, { is_active }),
  delete: (id) => api.delete(`/users/${id}`),
};

// Contacts API
export const contactsAPI = {
  list: (params) => api.get('/contacts', { params }),
  listWithStats: (params) => api.get('/contacts', { params: { ...params, include_stats: true } }),
  get: (id) => api.get(`/contacts/${id}`),
  getProfile: (id) => api.get(`/contacts/${id}/profile`),
  create: (data) => api.post('/contacts', data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
};

// Opportunities API
export const opportunitiesAPI = {
  list: (params) => api.get('/opportunities', { params }),
  get: (id) => api.get(`/opportunities/${id}`),
  create: (data) => api.post('/opportunities', data),
  update: (id, data) => api.put(`/opportunities/${id}`, data),
  delete: (id) => api.delete(`/opportunities/${id}`),
};

// Quotes API
export const quotesAPI = {
  list: (params) => api.get('/quotes', { params }),
  get: (id) => api.get(`/quotes/${id}`),
  create: (data) => api.post('/quotes', data),
  update: (id, data) => api.put(`/quotes/${id}`, data),
  delete: (id) => api.delete(`/quotes/${id}`),
  convertToInvoice: (id) => api.post(`/quotes/${id}/convert-to-invoice`),
  // PDF methods
  extractPdf: (file, useOcr = false) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/quotes/extract-pdf?use_ocr=${useOcr}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  createWithPdf: (data, pdfFile) => {
    const formData = new FormData();
    formData.append('contact_id', data.contact_id);
    formData.append('items', JSON.stringify(data.items));
    formData.append('tax_rate', data.tax_rate);
    if (data.valid_until) formData.append('valid_until', data.valid_until);
    if (data.notes) formData.append('notes', data.notes);
    if (pdfFile) formData.append('pdf_file', pdfFile);
    return api.post('/quotes/with-pdf', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadPdf: (quoteId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/quotes/${quoteId}/upload-pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadPdf: (quoteId) => api.get(`/quotes/${quoteId}/download-pdf`, {
    responseType: 'blob',
  }),
  deletePdf: (quoteId) => api.delete(`/quotes/${quoteId}/pdf`),
};

// Invoices API
export const invoicesAPI = {
  list: (params) => api.get('/invoices', { params }),
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Products API
export const productsAPI = {
  list: (params) => api.get('/products', { params }),
  get: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

// Tickets API
export const ticketsAPI = {
  list: (params) => api.get('/tickets', { params }),
  get: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  update: (id, data) => api.put(`/tickets/${id}`, data),
  delete: (id) => api.delete(`/tickets/${id}`),
  getMessages: (id) => api.get(`/tickets/${id}/messages`),
  addMessage: (id, data) => api.post(`/tickets/${id}/messages`, data),
};

// Audit Logs API (admin only)
export const auditLogsAPI = {
  list: (params) => api.get('/audit-logs', { params }),
  count: () => api.get('/audit-logs/count'),
};

export default api;
